import { useState, useRef } from 'preact/hooks'
import { integrationsApi } from '@/api/integrations'
import { useToast } from '@/hooks/useToast'
import type { IntegrationConnectionViewDto } from '@/types/integrations'

interface UseResetConnectionParams {
  clientCompanyId: string
  connectionId: string | undefined
  onConnectionUpdated: (connectionId: string, updates: Partial<IntegrationConnectionViewDto>) => void
  onError?: (errorMessage: string) => void
  // Called when the reset response includes a rotated plaintext key (Webhook
  // provider). The panel shows this exactly once via the one-time reveal modal.
  onKeyRotated?: (plaintextKey: string) => void
}

interface UseResetConnectionReturn {
  resetting: boolean
  showResetConfirm: boolean
  setShowResetConfirm: (show: boolean) => void
  resetConnection: () => Promise<void>
  clearResetState: () => void
}

export function useResetConnection({
  clientCompanyId,
  connectionId,
  onConnectionUpdated,
  onError,
  onKeyRotated,
}: UseResetConnectionParams): UseResetConnectionReturn {
  const [resetting, setResetting] = useState(false)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const cleanupRef = useRef<(() => void) | null>(null)
  const { showSuccess, showError } = useToast()

  const clearResetState = () => {
    if (cleanupRef.current) {
      cleanupRef.current()
      cleanupRef.current = null
    }
    setResetting(false)
    setShowResetConfirm(false)
  }

  const resetConnection = async () => {
    if (!clientCompanyId || !connectionId) return
    setResetting(true)
    let cancelled = false

    cleanupRef.current = () => {
      cancelled = true
    }

    try {
      const res = await integrationsApi.resetIntegrationConnection(clientCompanyId, connectionId)

      if (cancelled) return

      if (res.error) {
        if (res.status === 404) {
          showError('Reset Failed', 'Integration not found.')
        } else {
          onError?.(res.error)
        }
        return
      }

      onConnectionUpdated(connectionId, {
        Status: 'Active',
        StatusMessage: null,
        ConsecutiveFailureCount: 0,
        StatusUpdatedUtc: res.data?.StatusUpdatedUtc,
      })

      // Webhook-provider reset rotates the key and returns the new plaintext.
      // Hand it to the panel so the one-time reveal modal can show it.
      if (res.data?.ApiKey) {
        onKeyRotated?.(res.data.ApiKey)
      } else {
        showSuccess('Integration Reset', 'The connection has been reactivated and will retry on the next sync cycle.')
      }
    } catch (error) {
      if (!cancelled) {
        showError('Reset Failed', 'Failed to reset connection.')
      }
    } finally {
      if (!cancelled) {
        setResetting(false)
        setShowResetConfirm(false)
        cleanupRef.current = null
      }
    }
  }

  return {
    resetting,
    showResetConfirm,
    setShowResetConfirm,
    resetConnection,
    clearResetState,
  }
}
